/**
 *
 * FaqItemSmall
 *
 */

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import HeartEmptyIcon from 'components/SaveAsFavorite/images/heart-empty-white.svg';
import HeartFullIcon from 'components/SaveAsFavorite/images/heart-full-white.svg';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Badge from 'components/Badge';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { trackFavoriteAction } from 'containers/Favorites/utils';
import { makeSelectAllowSignIn } from 'containers/Main/selectors';
import { saveResource } from 'containers/Resources/actions';
import { makeSelectSavedResources } from 'containers/Resources/selectors';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import { isResourceSaved } from 'containers/Resources/utils';
import Mixpanel from 'utils/mixpanelService';
import { getImageFile, isBot } from 'utils/stringUtils';
import UserInteractionService from 'utils/userInteractionService';

const COLORS = ['#7A3A8C', '#A02B57', '#B88914', '#3966AA'];

const useStyles = makeStyles(theme => ({
  wrapper: {
    position: 'relative',
  },
  openWrapper: {
    width: '200%',
    zIndex: 8,
    left: '-50%',
  },
  rightWrapper: {
    left: 0,
  },
  leftWrapper: {
    left: '-100%',
  },
  link: {
    textDecoration: 'none',
    position: 'relative',
  },
  container: {
    overflow: 'hidden',
    borderRadius: '5px !important',
    border: `1px solid #E9ECED`,
    height: 271,
    width: 'auto',
    position: 'relative',
    backgroundColor: '#FFFFFF',
  },
  openContainer: {
    boxShadow: theme.shadows[16],
    border: 0,
  },
  contentWrapper: {
    padding: '2px 10px 8px 10px',
    zIndex: 1,
    height: '100%',
    position: 'relative',
  },
  badgeAndIconWrapper: {
    position: 'relative',
    marginBottom: 4,
  },
  iconButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    display: 'flex',
    padding: 4,
    marginRight: -4,
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  icon: {
    width: 17,
    zIndex: 2,
  },
  desktopWrapper: {
    zIndex: 0,
    height: '100%',
    '& > div': {
      height: '100%',
    },
  },
  questionWrapper: {
    marginTop: 20,
  },
  questionLabel: {
    opacity: 0.6,
    color: '#FFFFFF',
    fontSize: '0.875rem',
  },
  question: {
    color: '#FFFFFF',
    fontSize: '0.875rem',
    marginLeft: 10,
    display: '-webkit-box',
    maxHeight: 160,
    '-webkit-line-clamp': '9',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  darkOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    zIndex: -2,
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
}));

const FaqItemSmall = ({
  index,
  item,
  item: { article = '', slug, allTopics, topics = [] },
  listTitle,
  recommendation,
  expanded,
  grayscale,
  trackingPath,
}) => {
  const [open] = useState(expanded);
  const hasTracked = useRef(false);
  const wrapperRef = useRef();
  const dispatch = useDispatch();
  const location = useLocation();
  const auth = useSelector(makeSelectAuth());
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const savedResources = useSelector(makeSelectSavedResources());
  const locale = useAlgoliaLocale();
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const classes = useStyles();
  const colorIndex = useRef(index % 4);
  const finalTopics = !_isEmpty(allTopics)
    ? allTopics
    : topics.map(topic => ({
        title: _get(topic, 'fields.title'),
        slug: _get(topic, 'fields.slug'),
        imageUrl: getImageFile(topic.fields),
      }));

  const saved = isResourceSaved(savedResources, {
    id: item.objectID || item.id,
  });

  const handleSave = e => {
    e.preventDefault();
    e.stopPropagation();

    const id = item.objectID || item.id;

    let nodeID;
    if (saved) [{ nodeID }] = savedResources.filter(el => el.resourceID === id);

    const saveData = {
      nodeID,
      resource: {
        type: 'FAQ',
        resourceID: id,
        slug,
        name: article,
        ...(trackingPath && { path: trackingPath }),
      },
      save: !saved,
    };
    trackFavoriteAction('Resource Card', saveData.resource, auth, saved);
    dispatch(saveResource(saveData));
  };

  const redirect = () => {
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track('Resource Card - Clicked', {
      name: article,
      type: 'faq',
      page: finalPageName,
      path: trackingPath || location.pathname.split('/')[1],
      slug: location.pathname.split('/')[2],
      recommendation,
      listTitle,
      subtype: undefined,
      blog: false,
      entryId: item.objectID || item.id,
    });
    // clickListCardHandler({ name: article, type: 'faq' });
  };

  const backgroundImage = _get(
    finalTopics.filter(topic => topic.imageUrl),
    '0.imageUrl',
  );
  const cardRootStyle = backgroundImage
    ? {
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        position: 'absolute',
        opacity: 0.1,
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
      }
    : {};
  const cardRootColor = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: COLORS[colorIndex.current],
    zIndex: -2,
  };

  if (grayscale) {
    cardRootColor.filter = 'grayscale(1)';
  }

  const bot = isBot();

  const resourceAudiences = [];
  _get(item, 'audienceType', _get(item, 'audience', [])).forEach(entry => {
    if (
      _has(entry, 'fields.reviewStatus') &&
      _get(entry, 'fields.reviewStatus') !== 'Accepted'
    )
      return;

    const audienceName = _get(entry, 'fields.name', entry);
    resourceAudiences.push(audienceName);
  });

  return (
    <div
      className={_classNames(classes.wrapper, {
        [classes.openWrapper]: !expanded && open,
      })}
      ref={wrapperRef}
    >
      <Link to={`/faqs/${slug}`} className={classes.link} onClick={redirect}>
        <Paper
          elevation={0}
          className={_classNames(classes.container, {
            [classes.openContainer]: open,
          })}
          onMouseEnter={() => {
            if (!hasTracked.current) {
              hasTracked.current = true;
              UserInteractionService.registerGeneralInteraction({
                type: 'resource-card-hovered',
                resourceID: item.objectID || item.id,
                resourceAudiences: resourceAudiences.join(),
                resourceTags: null,
                resourceType: 'faq',
              });
            }
          }}
        >
          <div className={classes.desktopWrapper}>
            <Grid container>
              <Grid item xs={12} className={classes.contentWrapper}>
                {backgroundImage && <div style={cardRootStyle} />}
                <div style={cardRootColor} />
                <div className={classes.darkOverlay} />
                <Grid container>
                  <Grid item xs={12} className={classes.badgeAndIconWrapper}>
                    <Badge
                      type="faq"
                      icon="faqWhite"
                      color="#FFFFFF"
                      removeMargin
                    />
                    {!bot && allowSignIn && (
                      <>
                        {saved && (
                          <IconButton
                            className={classes.iconButton}
                            onClick={handleSave}
                            disableRipple
                            disableFocusRipple
                            aria-label="Unsave"
                          >
                            <img
                              src={HeartFullIcon}
                              alt="Full"
                              className={classes.icon}
                            />
                          </IconButton>
                        )}
                        {!saved && (
                          <IconButton
                            className={classes.iconButton}
                            onClick={handleSave}
                            disableRipple
                            disableFocusRipple
                            aria-label="Save"
                          >
                            <img
                              src={HeartEmptyIcon}
                              alt="Empty"
                              className={classes.icon}
                            />
                          </IconButton>
                        )}
                      </>
                    )}
                  </Grid>
                  <Grid item xs={12} className={classes.questionWrapper}>
                    <Grid container>
                      <Grid item>
                        <Typography
                          variant="h6"
                          className={classes.questionLabel}
                        >
                          {_get(siteCopy, 'pageCopy.faqCardQuestionAbbr')}
                        </Typography>
                      </Grid>
                      <Grid item xs>
                        <Typography variant="h6" className={classes.question}>
                          {getAlgoliaLocalizedField(item, 'article', locale)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
        </Paper>
      </Link>
    </div>
  );
};

FaqItemSmall.propTypes = {
  index: PropTypes.number,
  item: PropTypes.object,
  listTitle: PropTypes.string,
  recommendation: PropTypes.bool,
  expanded: PropTypes.bool,
  grayscale: PropTypes.bool,
};

FaqItemSmall.defaultProps = Object.create(
  {
    recommendation: false,
    expanded: false,
  },
  {
    index: {
      enumerable: true,
      get: () => Math.floor(Math.random() * 4),
    },
  },
);

export default FaqItemSmall;
